import { useState } from 'react';
import { AlertTriangle, Copy, Check } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  Button,
} from '@/components/ui';

export interface SecretRevealLabels {
  title: string;
  warning: string;
  hint: string;
  copy: string;
  copied: string;
  confirm: string;
}

interface SecretRevealDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The cleartext secret — sourced from a mutation response, never a GET. Shown exactly once. */
  secret: string;
  onConfirm: () => void;
  labels: SecretRevealLabels;
}

/**
 * Generic Secret-once reveal modal (Spec 505, D-Reveal-Reuse). ONE reveal mechanism for both the
 * Salt (Inc 2) and the Click-Webhook secret (Inc 2b/3) — the only difference is the copy, injected
 * as `labels`. The cleartext lives solely in the caller's prop/component state, is never persisted
 * or logged, and `onInteractOutside` is blocked so it cannot vanish before it is saved.
 */
export function SecretRevealDialog({
  open,
  onOpenChange,
  secret,
  onConfirm,
  labels,
}: SecretRevealDialogProps) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(secret);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleConfirm = () => {
    setCopied(false);
    onConfirm();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" onInteractOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle>{labels.title}</DialogTitle>
          <DialogDescription className="sr-only">{labels.warning}</DialogDescription>
        </DialogHeader>

        <div
          role="alert"
          className="flex items-start gap-3 rounded-md border border-yellow-500/30 bg-yellow-500/10 p-3"
        >
          <AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0 text-warning" aria-hidden="true" />
          <p className="text-sm font-medium text-warning-foreground">{labels.warning}</p>
        </div>

        <div className="flex items-center gap-2 rounded-md border bg-muted/50 p-3">
          <code className="flex-1 break-all font-mono text-sm">{secret}</code>
          <Button
            variant="ghost"
            size="icon"
            className="flex-shrink-0"
            onClick={handleCopy}
            aria-label={copied ? labels.copied : labels.copy}
          >
            {copied ? (
              <Check className="h-4 w-4 text-success" aria-hidden="true" />
            ) : (
              <Copy className="h-4 w-4" aria-hidden="true" />
            )}
          </Button>
        </div>

        <p className="text-xs text-muted-foreground">{labels.hint}</p>

        <DialogFooter>
          <Button onClick={handleConfirm} className="w-full">
            {labels.confirm}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
